import React, {useCallback, useRef, useState, useEffect} from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { buildChartTooltip, updateChartTheme, valueFormatted } from "../../../utils/helpsers";
import { BubbleStyles } from "./BubbleChart.style";
import PropTypes from "prop-types";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import TooltipCustom from "../../molecules/CustomTooltip/CustomTooltip";
import {Slider} from "@mui/material";
import { red } from '@mui/material/colors';

require("highcharts/modules/accessibility")(Highcharts);

/**
 * Bubble chart using highcharts library.
 * @param {object} x - X Axis of the chart.
 * @param {object, array} xCustomRange - Custom Range of X axis.
 * @param {object} y - Y Axis of the chart..
 * @param {object, array} yCustomRange - Custom Range of Y axis.
 * @param {object} z - Label of Z axis.
 * @param {number} bubbleSize - Size of the bubbles.
 * @param {object, array} options - Districts available.
 * @param {number} year - Year set by the user.
 */

const BubbleChart = ({x, xCustomRange, y, yCustomRange, z, bubbleSize, options, year}) => {

  const [zNegative, setZNegative] = useState(false);

  useEffect(()=> {
    if(options?.some( opt => opt.z < 0 )) {
      setZNegative(true)
    }
  }, [options])

  // Build X axis title based on var has definition.
  let xDescription = x.description;
  let xAxisTitle = xDescription
    ? `<span class="axisLabel">${x.label}</span><span class="infoIcon" id="bubble-axis-x">ⓘ</span><span class="tooltip-desc" style="visibility: hidden">${xDescription}</span>`
    : x.label;
  // Build Y axis title based on var has definition.
  let yDescription = y.description;
  let yAxisTitle = yDescription
    ? `<span class="axisLabel">${y.label}</span><span class="infoIcon" id="bubble-axis-y">ⓘ</span><span class="tooltip-desc" style="visibility: hidden">${yDescription}</span>`
    : y.label;

  let showLabelsHelpText = "Names may overlap or disappear if too many points are being displayed"

  // Build Z axis label
  const attachTooltipDescription = useCallback(()=> {
    // Set Tooltip if var description is available.
    if (xDescription) {
      let xInfoIcon = document.querySelector(".highcharts-axis #bubble-axis-x");
      buildChartTooltip({ infoIcon: xInfoIcon });
    }
    if (yDescription) {
      let yInfoIcon = document.querySelector(".highcharts-axis #bubble-axis-y");
      buildChartTooltip({ infoIcon: yInfoIcon, styles:"left: 100%; transform-origin: top left; transform: rotate(90deg);" });
    }
  },[xDescription, yDescription])

  // Create a ref to store the chart instance
  const chartRef = useRef(null);

  const BubbleData = {
    chart: {
      type: "bubble",
      height: 620,
      backgroundColor: '#fff',
      plotBorderWidth: 1,
      zoomType: "xy",
      events: {
        render: function () {
          let points = this.series[0].points;
          points.forEach((point) => {
            if (point.selected === true) {
              this.tooltip.refresh(point);
            }
          });
          // Building Tooltips
          attachTooltipDescription();
        },
      },
      style: {
        fontFamily: ["Titillium Web", "sans-serif"],
      },
      className: "highcharts--scatter-chart",
    },
    legend: {
      enabled: false,
    },
    title: {
      text: "",
    },
    subtitle: {
      text: "",
    },
    accessibility: {
      point: {
        valueDescriptionFormat:
          "{index}. {point.name}, fat: {point.x}g, sugar: {point.y}g, obesity: {point.z}%.",
      },
    },
    xAxis: {
      gridLineWidth: 1,
      title: {
        useHTML: true,
        text: xAxisTitle,
        style: {
          color: "#333",
        }
      },
      labels: {
        format: "{value}",
        style: {
          color: "#333",
        }
      },
      accessibility: {
        rangeDescription: "",
      },
      plotLines: [
        {
          dashStyle: "shortdash",
          value: xCustomRange ? xCustomRange[0] : 0,
          width: xCustomRange ? 2 : 0,
          color: "#00D7BE",
        },
        {
          dashStyle: "shortdash",
          value: xCustomRange ? xCustomRange[1] : 0,
          width: xCustomRange ? 2 : 0,
          color: "#00D7BE",
        },
      ],
    },
    yAxis: {
      startOnTick: false,
      endOnTick: false,
      title: {
        useHTML: true,
        text: yAxisTitle,
        style: {
          color: "#333",
        }
      },
      labels: {
        format: "{value}",
        style: {
          color: "#333",
        }
      },
      maxPadding: 0.2,
      accessibility: {
        rangeDescription: "",
      },
      plotLines: [
        {
          dashStyle: "shortdash",
          value: yCustomRange ? yCustomRange[0] : 0,
          width: yCustomRange ? 2 : 0,
          color: "#00D7BE",
        },
        {
          dashStyle: "shortdash",
          value: yCustomRange ? yCustomRange[1] : 0,
          width: yCustomRange ? 2 : 0,
          color: "#00D7BE",
        },
      ],
    },
    tooltip: {
      useHTML: true,
      headerFormat: '<div>',
      pointFormatter: function () {
        let xValue = valueFormatted(this.x, x.var_type);
        let yValue = valueFormatted(this.y, y.var_type);
        let zValue = valueFormatted(this.z_negative ?? this.z, z.var_type);
        return `<div><strong>${this.district_name}</strong></div>
                <div><span>${x.label}: </span><strong>${xValue}</strong></div>
                <div><span>${y.label}: </span><strong>${yValue}</strong></div>
                <div><span>${z.label}: </span><strong> ${zValue}</strong></div>`;
      },
      footerFormat: '</div>',
      followPointer: true,
      backgroundColor: "#FFF",
      hideDelay: 0,
    },
    plotOptions: {
      series: {
        point: {
          events: {
            mouseOver: function () {
              const labels = document.querySelector(
                ".highcharts--scatter-chart div.highcharts-yaxis"
              );
              labels?.classList?.add("to-back");
            },
            mouseOut: function () {
              const labels = document.querySelector(
                ".highcharts--scatter-chart div.highcharts-yaxis"
              );
              labels?.classList?.remove("to-back");
            },
          },
        },
        dataLabels: {
          enabled: false,
          format: '{point.district_name}',
          y:-10,
          overflow: true,
          crop: false,
          inside: false,
          allowOverlap: true,
        },
      },
    },
    exporting: {
      tableCaption: false,
      buttons: {
        contextButton: {
          menuItems: [
            "viewFullscreen",
            {
              text: "Toggle Light/Dark mode",
              onclick: function () {
                updateChartTheme(this);
              }
            },
            {
              separator: true,
            },
            {
              text: "Download PDF document",
              onclick: function () {
                window._paq.push([
                  "trackEvent",
                  "Board DistrictExplorer",
                  "Downloaded: PDF in Bubble chart",
                ]);
                this.exportChart({
                  type: "application/pdf",
                });
              },
            },
            {
              text: "Download CSV",
              onclick: function () {
                window._paq.push([
                  "trackEvent",
                  "Board DistrictExplorer",
                  "Downloaded: CSV in Bubble chart",
                ]);
                this.downloadCSV();
              },
            },
            {
              text: "Download XLS",
              onclick: function () {
                window._paq.push([
                  "trackEvent",
                  "Board DistrictExplorer",
                  "Downloaded: XLS in Bubble chart",
                ]);
                this.downloadXLS();
              },
            },
          ],
        },
      },
      csv: {
        columnHeaderFormatter: function (item, key) {
          if (!item || item instanceof Highcharts.Axis) {
            return x.label;
          } else {
            return item.name;
          }
        },
      },
    },
    series: [
      {
        data: options.map( opt => {
          // Z axis for negative numbers is override by zero to estandarice sizes
          // The original Z value is stored in a new var to be displayed in the tooltips
          if(opt.z < 0 ) {
            return {
              ...opt,
              z_negative: opt.z,
              z: 0,
              color: opt.color ?? red[600]
            }
          } else {
            return  {
              ...opt,
              color: opt.color ?? "#4646FF",
            }
          }
        }),
      },
    ],
  };

  // Function to handle button click and update the chart
  const displayLabels = () => {
    if (chartRef.current) {
      let labelsEnabled = chartRef.current.chart.userOptions.plotOptions.series.dataLabels.enabled;
      chartRef.current.chart.update({
        plotOptions: {
          series: {
            dataLabels: {
              enabled: !labelsEnabled
            }
          }
        },
      });
    }
  };

  // Function to control the bubble size
  const handleBubbleSize = (e) => {
    chartRef.current.chart.update({
      plotOptions: {
        bubble: {
          maxSize: e.target.value,
        }
      },
    });
  };

  switch (options) {
    case "loading":
      return (
        <Box>
          <CircularProgress size={80} />
        </Box>
      );
    case "no-data":
      return <div >MISSING PARAMETER</div>;
    default:
      return (
      <Box sx={BubbleStyles}>
        <HighchartsReact ref={chartRef} highcharts={Highcharts} options={BubbleData} />
        <div className={"zAxis-tools"}>
         <FormGroup>
           <FormControlLabel className="control-labels" control={<Switch onChange={displayLabels} />} label="Show labels" />
         </FormGroup>
         <TooltipCustom
             text={showLabelsHelpText}
             aria-label={showLabelsHelpText}
         />
         <div className="bubbleSize-container">
           <span className="bubbleSize__label">Bubble Size: </span>
           <Slider
               arial-label="Bubble Size"
               size="small"
               defaultValue={50}
               max={100}
               min={0}
               step={25}
               onChange={handleBubbleSize}
               valueLabelDisplay="auto"
               marks={[
                 {
                   value: 0,
                   label: 'Min',
                 },
                 {
                   value: 25,
                   label: '25%',
                 },
                 {
                   value: 50,
                   label: '50%',
                 },
                 {
                   value: 75,
                   label: '75%',
                 },
                 {
                   value: 100,
                   label: 'Max',
                 },
               ]}
           />
         </div>
       </div>
        <div className="z-axis-description">
          <span>Bubble size represents the <strong>{z.label}</strong></span>
          {
              z.description && <TooltipCustom text={z.description} />
          }
        </div>
        {
            zNegative && <div className={"z-axis-negative"}>Red bubbles represent negative <strong>{z.label}</strong> values</div>
        }
      </Box>
      )
  }
};

BubbleChart.propTypes = {
  x: PropTypes.object.isRequired,
  xCustomRange: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.array.isRequired,
  ]),
  y: PropTypes.object.isRequired,
  yCustomRange: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.array.isRequired,
  ]),
  z: PropTypes.object.isRequired,
  options: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.array.isRequired,
  ]),
}

export default React.memo(BubbleChart);
